import { useState, useCallback, useRef } from 'react';
import Upload from '$/utils/upload';
import { setUploadThread } from '$/store/useUploadThreadStore';
import { setUploadErrorToast } from '$/store/useUploadErrorToastStore';

const MAX_PER_BATCH = 20;
const MAX_TOTAL = 30;
const DEFAULT_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
const DEFAULT_MAX_FILE_SIZE_MB = 30;

export interface UploadedImage {
  uri: string;
  url: string;
  sourceFile?: File;
  sourceIndex?: number;
}

interface UseImageUploadOptions {
  maxCount?: number;
  /** 允许的 MIME 类型列表，默认仅图片 */
  acceptedTypes?: string[];
  /** 单文件大小上限（MB），支持固定值或按文件动态返回 */
  maxFileSize?: number | ((file: File) => number);
  onSuccess?: (images: UploadedImage[]) => void;
  onError?: (error: Error) => void;
  afterUpload?: (images: UploadedImage[], files: File[]) => Promise<UploadedImage[]> | UploadedImage[];
  hideProgressMessage?: boolean;
}

/** AI 工具共用的批量上传状态，复用 SDK 上传服务及全局进度提示。 */
export default function useImageUpload(options: UseImageUploadOptions = {}) {
  const {
    maxCount = MAX_TOTAL,
    acceptedTypes = DEFAULT_ACCEPTED_TYPES,
    maxFileSize = DEFAULT_MAX_FILE_SIZE_MB,
    onSuccess,
    onError,
    afterUpload,
    hideProgressMessage = false,
  } = options;

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const uploadInstanceRef = useRef<Upload | null>(null);
  const uploadingRef = useRef(false);

  const optionsRef = useRef({ onSuccess, onError, afterUpload });
  optionsRef.current = { onSuccess, onError, afterUpload };

  const validateFiles = useCallback(
    (files: File[]): File[] => {
      const validFiles = files.filter((file) => {
        const lowerName = file.name.toLowerCase();
        return acceptedTypes.some((type) => {
          if (type.startsWith('.')) return lowerName.endsWith(type);
          if (type.endsWith('/*')) return file.type.startsWith(type.replace('/*', '/'));
          if (type === 'image/jpg') return lowerName.endsWith('.jpg') || file.type === 'image/jpeg';
          return file.type === type;
        });
      });
      if (validFiles.length === 0) {
        throw new Error('不支持所选文件格式');
      }
      const slicedFiles = validFiles.slice(0, maxCount);

      const oversizeFiles: File[] = [];
      const passedFiles = slicedFiles.filter((file) => {
        const limitMB = typeof maxFileSize === 'function' ? maxFileSize(file) : maxFileSize;
        if (file.size > limitMB * 1024 * 1024) {
          oversizeFiles.push(file);
          return false;
        }
        return true;
      });

      if (oversizeFiles.length > 0) {
        const sampleLimitMB = typeof maxFileSize === 'function'
          ? maxFileSize(oversizeFiles[0])
          : maxFileSize;
        setUploadErrorToast(true, [
          {
            message: {
              key: 'RoboNeo_Web_upload_image_size_maximum',
              params: { max_size: sampleLimitMB },
            },
            fileNum: oversizeFiles.length,
          },
        ]);
      }

      if (passedFiles.length === 0) {
        const fallbackMB = typeof maxFileSize === 'function' ? DEFAULT_MAX_FILE_SIZE_MB : maxFileSize;
        throw new Error(`所有文件均超过大小限制 ${fallbackMB}MB`);
      }

      return passedFiles;
    },
    [maxCount, acceptedTypes, maxFileSize],
  );

  const uploadImages = useCallback(
    async (files: File[]): Promise<UploadedImage[] | null> => {
      if (uploadingRef.current) return null;

      let validFiles: File[];
      try {
        validFiles = validateFiles(files);
      } catch (err) {
        optionsRef.current.onError?.(err as Error);
        return null;
      }

      uploadingRef.current = true;
      setUploading(true);
      setProgress(0);

      const uploader = Upload.getInstance();
      uploader.init();
      uploadInstanceRef.current = uploader;

      const totalCount = validFiles.length;
      const threadId = String(uploader.uploadTaskId);

      if (!hideProgressMessage) {
        setUploadThread(threadId, {
          total: totalCount,
          done: 0,
        });
      }

      const batches: File[][] = [];
      for (let i = 0; i < totalCount; i += MAX_PER_BATCH) {
        batches.push(validFiles.slice(i, i + MAX_PER_BATCH));
      }

      const allResults: UploadedImage[] = [];
      let completedInPrevBatches = 0;
      let batchError: Error | null = null;

      // progressChange 回调中 batchPercent 最大值为 percent + 20，即约 120
      const BATCH_PERCENT_MAX = 120;

      // eslint-disable-next-line no-await-in-loop
      for (let batchIdx = 0; batchIdx < batches.length; batchIdx += 1) {
        const batch = batches[batchIdx];
        const batchOffset = completedInPrevBatches;

        try {
          // eslint-disable-next-line no-await-in-loop
          const results = await uploader.uploadMultiple(
            batch,
            { hideProgressMessage },
            (batchPercent: number) => {
              const batchDoneRatio = batchOffset / totalCount;
              const batchWeight = batch.length / totalCount;
              const overallPercent = Math.min(
                Math.round((batchDoneRatio + batchWeight * (batchPercent / BATCH_PERCENT_MAX)) * 100),
                99,
              );
              setProgress(overallPercent);
            },
          );

          if (!results) {
            batchError = new Error(`第 ${batchIdx + 1} 批上传失败`);
            break;
          }

          allResults.push(...results.map((item, itemIndex) => {
            const sourceIndex = batchOffset + itemIndex;
            return {
              uri: item.uri,
              url: item.url || '',
              sourceFile: validFiles[sourceIndex],
              sourceIndex,
            };
          }));
          completedInPrevBatches += batch.length;
        } catch (err) {
          batchError = err as Error;
          break;
        }
      }

      let finalResults = allResults;
      if (allResults.length > 0 && optionsRef.current.afterUpload) {
        try {
          finalResults = await optionsRef.current.afterUpload(allResults, validFiles);
        } catch (err) {
          finalResults = [];
          batchError = err as Error;
        }
      }

      if (!hideProgressMessage) {
        setUploadThread(threadId, { detected: true });
      }

      if (finalResults.length > 0) {
        setProgress(batchError ? Math.round((finalResults.length / totalCount) * 100) : 100);
        setUploadedImages((prev) => [
          ...prev,
          ...finalResults.map(({ uri, url, sourceIndex }) => ({ uri, url, sourceIndex })),
        ]);
      }

      uploadingRef.current = false;
      setUploading(false);
      uploadInstanceRef.current = null;

      if (batchError) {
        optionsRef.current.onError?.(batchError);
        return finalResults.length > 0 ? finalResults : null;
      }

      optionsRef.current.onSuccess?.(finalResults);
      return finalResults;
    },
    [validateFiles, hideProgressMessage],
  );

  const cancelUpload = useCallback(() => {
    if (uploadInstanceRef.current) {
      const threadId = String(uploadInstanceRef.current.uploadTaskId);
      if (!hideProgressMessage) {
        setUploadThread(threadId, { canceled: true });
      }
      uploadInstanceRef.current.onCancelUpload();
      uploadInstanceRef.current = null;
      uploadingRef.current = false;
      setUploading(false);
      setProgress(0);
    }
  }, [hideProgressMessage]);

  const removeImage = useCallback((index: number) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearImages = useCallback(() => {
    setUploadedImages([]);
  }, []);

  return {
    uploading,
    progress,
    uploadedImages,
    uploadImages,
    cancelUpload,
    removeImage,
    clearImages,
    setUploadedImages,
  };
}
